// @ts-check
import ZeroDataGrid from '~/components/Shared/ZeroDataGrid/ZeroDataGrid';
import { useDataGridSetup } from '~/components/Shared/ZeroDataGrid/ZeroDataGrid.hooks';
import useZeroSelector from '~/hooks/useZeroSelector';
import { isAdminOrTeamLead, objectIsEmpty, setGridLoading } from '~/other/Helper';
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAssignmentDataGridContext } from '~/components/Forms/AssignmentDataGrid/AssignmentDataGrid.context';
import { AssignmentDataGridModals } from '~/components/Forms/AssignmentDataGrid/AssignmentDataGrid.modals';
import { EventNames, getColumnDefs, getSortByColumnName, loadAssignments } from '~/components/Forms/AssignmentDataGrid/AssignmentDataGrid.utils';
import { useCurrentUser } from '~/hooks/reduxHooks';

const ASSIGNMENTS_PER_PAGE = 25;

function useAssignmentDataGridQuery() {
    const formsQuery = useZeroSelector(state => state.forms.query);
    const periodQuery = useZeroSelector(state => state.forms.period_query);
    const [query, setQuery] = useState({ formsQuery, periodQuery });

    useEffect(() => {
        setQuery({ formsQuery, periodQuery });
    }, [formsQuery, periodQuery]);

    return query;
}

/**
 * @param {{
 *  pageType: string,
 * }} props
 */
export default function AssignmentDataGrid({ pageType }) {
    const location = useLocation();
    const { gridRef, settings, setSettings, rowCount } = useAssignmentDataGridContext();
    const query = useAssignmentDataGridQuery();
    const user = useCurrentUser();
    const totalAssignmentsRef = useRef(-1);

    const colDefs = useMemo(() => getColumnDefs(pageType, isAdminOrTeamLead(user)), [pageType, user]);

    useEffect(() => {
        totalAssignmentsRef.current = -1;
    }, [query.formsQuery, query.periodQuery]);

    const getRows = useCallback(async (/** @type {AgGrid.IGetRowsParams} */params) => {
        const perPage = ASSIGNMENTS_PER_PAGE;
        const currentPage = Math.floor((params.startRow ?? 0) / perPage) + 1;

        const { formsQuery, periodQuery } = query;

        const queryParams = new URLSearchParams(formsQuery + periodQuery);
        queryParams.set('per_page', String(perPage));
        queryParams.set('page', String(currentPage));
        queryParams.set('full_data', 'false');
        queryParams.set('form_types', pageType);

        if (totalAssignmentsRef.current !== -1 && params.startRow >= totalAssignmentsRef.current) {
            params.successCallback([], totalAssignmentsRef.current);
            return;
        }

        if (!objectIsEmpty(params.filterModel)) {
            queryParams.set('ag_filter', JSON.stringify(params.filterModel));
        }

        params.sortModel.forEach(sortModel => {
            let col = getSortByColumnName(sortModel.colId);
            if (sortModel.sort === 'desc') {
                col = `-${col}`;
            }
            queryParams.append('sort_by', col);
        });

        const gridApi = gridRef.current?.api;
        if (rowCount === null) {
            setGridLoading(gridApi, true);
        }
        
        const { assignments, totalAssignments } = await loadAssignments(queryParams);

        totalAssignmentsRef.current = totalAssignments;
        params.successCallback(assignments, totalAssignments);
        if (rowCount === null) {
            setGridLoading(gridApi, false);
        }
    }, [rowCount, query, pageType, totalAssignmentsRef]);

    const clearAssignmentCache = useCallback(() => {
        // @ts-ignore
        loadAssignments.cache.clear();
    }, []);

    const { onGridReady, onStateUpdated } = useDataGridSetup({
        eventNames: EventNames,
        autoLoadData: true,
        gridRef,
        colDefs,
        getRows,
        setSettings,
        onReset: clearAssignmentCache,
        onRefresh: clearAssignmentCache,
    });
    
    useEffect(() => {
        // Clear memoization when component is unmounted
        return () => {
            clearAssignmentCache();
        }
    }, []);

    return (
        <>
            <ZeroDataGrid
                gridRef={gridRef}
                columnDefs={colDefs}
                defaultColDef={{
                    cellStyle: {
                        display: 'flex',
                        alignItems: 'center',
                    }
                }}
                initialState={location.state?.gridState ?? settings}
                onGridReady={onGridReady}
                onStateUpdated={onStateUpdated}
                cacheBlockSize={ASSIGNMENTS_PER_PAGE}
                cacheOverflowSize={2}
                maxConcurrentDatasourceRequests={2}
                infiniteInitialRowCount={rowCount ?? ASSIGNMENTS_PER_PAGE}
                pagination={true}
                paginationPageSizeSelector={false}
                paginationPageSize={rowCount ?? ASSIGNMENTS_PER_PAGE}
                tooltipShowDelay={500}
                tooltipShowMode="standard"
                suppressCellFocus
            />
            <AssignmentDataGridModals />
        </>
    );
}
